import { DateTime } from 'luxon';

import { formatPcDate } from 'app/shared/utils/format-date';

/**
 * Secondary line of a notification row: a catalog key for the `| translate`
 * pipe, or `text` to show exactly as the backend wrote it.
 */
export interface NotificationDetail {
  labelKey?: string;
  params?: Record<string, string | number>;
  /**
   * Shown as is: a record name ("Cliente — Remodelación local") or a date in a
   * format the bell does not read. Never sent to the translator, so a name that
   * happens to match a catalog key is not replaced by its translation.
   */
  text?: string;
  /** Amber while a deadline approaches (mirrors `expiration-date-cell`). */
  warning?: boolean;
  /** Deep red once a deadline has passed. */
  danger?: boolean;
}

/** A notification description split into what it's about and what happened. */
export interface NotificationParts {
  /** The document name or folio — the part that distinguishes one row. */
  subject: string;
  detail: NotificationDetail | null;
}

/**
 * Separator the backend puts between a record and its detail. Two kinds of
 * writers use it: the expiry scans put a due date after it, the assignment and
 * follow-up notices put a name ("{customer} — {opportunity}").
 */
const EXPIRY_SEPARATOR = ' — ';
/**
 * The only tail read as a deadline. Anything looser let a name be taken for one:
 * luxon reads "2026" or "2026-11" as dates, so an opportunity called "2026" was
 * shown as "Vencida" in red.
 */
const DUE_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Separator the offer and sales-order services put before the new status. */
const STATUS_SEPARATOR = ' → ';

/** Beyond this many days the deadline is context, not a warning. */
const WARNING_WINDOW_DAYS = 7;

/**
 * Splits a bell notification's description into a subject and a detail.
 *
 * The backend pre-assembles the description, so the shape is a contract with its
 * writers: `"{document} — {yyyy-MM-dd}"` from the expiry scans, `"{record} — {name}"`
 * from the assignment and follow-up notices, and `"{folio} → {STATUS}"` from the
 * offer and sales-order status changes. This is
 * why the row can promote the subject to the hero position and give the deadline
 * its own treatment — the type alone repeats across rows and says nothing about
 * which record needs attention.
 *
 * A description matching neither shape becomes the subject verbatim with no
 * detail, so an unrecognized (or newly added) notification still reads correctly
 * instead of disappearing. If a fourth writer appears with its own separator,
 * this is the one place to teach it.
 *
 * @param description The raw `description` from the notifications endpoint.
 * @param now Reference moment for the deadline math; defaults to the current
 *   time. Pass it to make tests deterministic.
 */
export function notificationParts(description: unknown, now?: DateTime): NotificationParts | null {
  if (typeof description !== 'string' || !description.trim()) {
    return null;
  }
  const text = description.trim();

  const expiryIndex = text.indexOf(EXPIRY_SEPARATOR);
  if (expiryIndex > 0) {
    const subject = text.slice(0, expiryIndex).trim();
    const rest = text.slice(expiryIndex + EXPIRY_SEPARATOR.length).trim();
    return { subject, detail: deadlineDetail(rest, now) };
  }

  const statusIndex = text.indexOf(STATUS_SEPARATOR);
  if (statusIndex > 0) {
    const subject = text.slice(0, statusIndex).trim();
    const status = text.slice(statusIndex + STATUS_SEPARATOR.length).trim();
    // The raw enum doubles as a translate key — APPROVED / ACCEPTED / DENIED and
    // friends are already in the catalog, and ngx-translate echoes the key for
    // any status that isn't, which still reads better than hiding it.
    return { subject, detail: status ? { labelKey: status } : null };
  }

  return { subject: text, detail: null };
}

/**
 * How a due date should read: expired, due today, closing in, or simply dated.
 *
 * Mirrors `expiration-date-cell`'s colour language so a deadline says the same
 * thing in the bell as it does in a table — amber inside the warning window, red
 * once it has passed, plain ink beyond.
 *
 * The wording, however, does NOT reuse the table's keys. `EXPIRES_ON` ("vence el
 * {{date}}") and `EXPIRING_IN_DAYS` ("en {{days}} días") are deliberately
 * lowercase fragments: they were written to be appended to something else — a
 * date cell's second line, the quotes list's `expirationSuffix`. Here the string
 * IS the whole line, and a line that opens mid-sentence reads as broken text.
 *
 * Capitalising those two keys was not an option twice over: it would break the
 * three call sites that append them, and the loader merges `{...local, ...remote}`,
 * so editing a key that already ships in a per-instance catalog is a no-op
 * anyway. Hence the dedicated `NOTIF_EXPIRES_*` keys — new keys take effect
 * immediately and belong to this one caller.
 *
 * The value is a date with no time, so it is parsed as a LOCAL calendar date.
 * `new Date('2026-08-26')` would land on UTC midnight and read a day early in
 * negative offsets, which is also why `DaysRemainingPipe` isn't used here.
 */
function deadlineDetail(value: string, now?: DateTime): NotificationDetail | null {
  const due = DUE_DATE.test(value) ? DateTime.fromISO(value) : null;
  if (!due?.isValid) {
    // A name, or not a date after all: show what the backend sent rather than nothing.
    return value ? { text: value } : null;
  }

  const reference = now ?? DateTime.now();
  const days = Math.floor(due.startOf('day').diff(reference.startOf('day'), 'days').days);

  if (days < 0) {
    return { labelKey: 'EXPIRED', danger: true };
  }
  if (days === 0) {
    return { labelKey: 'EXPIRES_TODAY', warning: true };
  }
  if (days <= WARNING_WINDOW_DAYS) {
    // Singular gets its own key, the way DAY_COUNT_ONE/OTHER already does it in
    // this catalog: "Vence en 1 días" is the kind of thing a plural-blind
    // interpolation says out loud on the one day it matters most.
    return {
      labelKey: days === 1 ? 'NOTIF_EXPIRES_IN_DAYS_ONE' : 'NOTIF_EXPIRES_IN_DAYS_OTHER',
      params: { days },
      warning: true,
    };
  }
  return {
    labelKey: 'NOTIF_EXPIRES_ON',
    params: { date: formatPcDate(due, 'date') ?? '' },
  };
}
